'use client'

import { Button, type ButtonProps } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import Link from 'next/link'
import React from 'react'
import { useParams } from 'next/navigation'

import type { Page, Post } from '@/payload-types'
import { useApplicationModalStore } from '@/stores/applicationModalStore'
import { trackHeroCtaClick, trackNavItemClick } from '@/utilities/analytics'

type TrackingType = 'hero_cta' | 'nav_item' | null

type CMSLinkType = {
  appearance?: 'inline' | ButtonProps['variant']
  children?: React.ReactNode
  className?: string
  label?: string | null
  newTab?: boolean | null
  reference?: {
    relationTo: 'pages' | 'posts'
    value: Page | Post | string | number
  } | null
  size?: ButtonProps['size'] | null
  type?: 'custom' | 'reference' | null
  url?: string | null
  trackAs?: TrackingType
}

export const CMSLink: React.FC<CMSLinkType> = (props) => {
  const {
    type,
    appearance = 'inline',
    children,
    className,
    label,
    newTab,
    reference,
    size: sizeFromProps,
    url,
    trackAs,
  } = props

  const params = useParams()
  const locale = params?.locale as string | undefined
  const { openModal } = useApplicationModalStore()

  let href =
    type === 'reference' && typeof reference?.value === 'object' && reference.value.slug
      ? `${reference?.relationTo !== 'pages' ? `/${reference?.relationTo}` : ''}/${
          reference.value.slug
        }`
      : url

  // Check if this is an application link (should open modal instead)
  const isApplicationLink = href === '/application' || href?.match(/^\/(en|uk)\/application/)

  // Add locale prefix to internal links if locale is present and href doesn't already have it
  if (href && locale && !href.startsWith('http') && !href.startsWith(`/${locale}`)) {
    // Don't add locale to links that already have a locale prefix
    const hasLocalePrefix = href.match(/^\/(en|uk)(\/|$)/)
    if (!hasLocalePrefix) {
      href = `/${locale}${href}`
    }
  }

  if (!href) return null

  const size = appearance === 'link' ? 'clear' : sizeFromProps
  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}

  // Handle application links - open modal instead of navigating
  const handleClick = (e: React.MouseEvent) => {
    // Track the click if trackAs is specified
    if (trackAs && label && href) {
      if (trackAs === 'hero_cta') {
        trackHeroCtaClick(label, href)
      } else if (trackAs === 'nav_item') {
        trackNavItemClick(label, href)
      }
    }

    if (isApplicationLink) {
      e.preventDefault()
      openModal()
    }
  }

  /* Ensure we don't break any styles set by richText */
  if (appearance === 'inline') {
    return (
      <Link
        className={cn(className)}
        href={href || url || ''}
        onClick={handleClick}
        {...newTabProps}
      >
        {label && label}
        {children && children}
      </Link>
    )
  }

  return (
    <Button
      asChild
      className={cn('rounded-full md:rounded-lg', className)}
      size={size}
      variant={appearance}
    >
      <Link href={href || url || ''} onClick={handleClick} {...newTabProps}>
        {label && label}
        {children && children}
      </Link>
    </Button>
  )
}
